import { useState, useEffect } from 'react'
import { UserIcon } from '@heroicons/react/24/outline'

interface RetryImageProps {
  src: string
  alt: string
  className?: string
  maxRetries?: number
  retryDelay?: number
  fallbackIcon?: React.ReactNode
}

export default function RetryImage({
  src,
  alt,
  className = "",
  maxRetries = 3,
  retryDelay = 2000,
  fallbackIcon
}: RetryImageProps) {
  const [currentSrc, setCurrentSrc] = useState(src)
  const [retryCount, setRetryCount] = useState(0)
  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setCurrentSrc(src)
    setRetryCount(0)
    setHasError(false)
    setIsLoading(true)
  }, [src])

  const handleError = () => {
    console.log(`Image load failed (attempt ${retryCount + 1}/${maxRetries + 1}):`, currentSrc)
    
    if (retryCount < maxRetries) {
      // Retry after delay
      setTimeout(() => {
        setRetryCount(prev => prev + 1)
        // Add cache-busting parameter to force reload
        const separator = currentSrc.includes('?') ? '&' : '?'
        setCurrentSrc(`${src}${separator}_retry=${retryCount + 1}&_t=${Date.now()}`)
      }, retryDelay)
    } else {
      console.error('Image failed to load after all retries:', currentSrc)
      setHasError(true)
      setIsLoading(false)
    }
  }

  const handleLoad = () => {
    console.log('Image loaded successfully:', currentSrc)
    setHasError(false)
    setIsLoading(false)
  }

  if (hasError) {
    return (
      <div className={`flex items-center justify-center bg-gray-200 dark:bg-gray-600 ${className}`}>
        {fallbackIcon || <UserIcon className="w-8 h-8 text-gray-400" />}
      </div>
    )
  }

  return (
    <>
      <img
        src={currentSrc}
        alt={alt}
        className={className}
        onError={handleError}
        onLoad={handleLoad}
      />
      {isLoading && (
        <div className={`absolute inset-0 flex items-center justify-center bg-gray-200 dark:bg-gray-600 ${className}`}>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
        </div>
      )}
    </>
  )
}
